import { useMemo } from 'react';
import { Button, Card, Col, Flex, Row, Tag, Typography, theme } from 'antd';
import { LuCheck, LuLayers, LuPentagon } from 'react-icons/lu';

import { useObjectPickerStore } from '@stores/useObjectPickerStore';
import { formatAoiArea, getAoiStats } from '@utils/aoiStats';
import { AREA_MODE } from '@utils/constants';

import ColorDot from '../ColorDot';

const { Text, Title } = Typography;

/**
 * A selectable area-scope card (Draw AOI / Entire project).
 */
const AreaOptionCard = ({ icon, title, badge, description, selected, onSelect }) => {
	const { token } = theme.useToken();

	return (
		<Card
			hoverable
			onClick={onSelect}
			role="button"
			tabIndex={0}
			aria-pressed={selected}
			onKeyDown={event => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					onSelect();
				}
			}}
			styles={{ body: { height: '100%' } }}
			style={{
				height: '100%',
				borderColor: selected ? token.colorPrimary : undefined,
				borderWidth: selected ? 2 : 1,
			}}>
			<Flex vertical gap={12} style={{ height: '100%' }}>
				<Flex
					align="center"
					justify="center"
					style={{
						width: 44,
						height: 44,
						borderRadius: token.borderRadiusLG,
						background: token.colorFillQuaternary,
						fontSize: 22,
						color: token.colorPrimary,
					}}>
					{icon}
				</Flex>
				<Flex align="center" gap={8} wrap>
					<Title level={5} style={{ margin: 0 }}>
						{title}
					</Title>
					{badge ? (
						<Tag
							variant="filled"
							color="green"
							style={{ fontSize: 10, letterSpacing: 0.5, margin: 0 }}>
							{badge}
						</Tag>
					) : null}
				</Flex>
				<Text type="secondary" style={{ fontSize: 13 }}>
					{description}
				</Text>
			</Flex>
		</Card>
	);
};

/**
 * Step 2 of the detection wizard: choose where detection runs — sketch an area
 * of interest on the map, or use the entire project.
 *
 * @param {() => void} props.onDraw - starts the OpenLayers draw flow (from the shell).
 */
const AreaStep = ({ onDraw }) => {
	const { token } = theme.useToken();
	const objects = useObjectPickerStore(s => s.selectedObjects);
	const sourceCollectionName = useObjectPickerStore(s => s.sourceCollectionName);

	const areaMode = useObjectPickerStore(s => s.areaMode);
	const setAreaMode = useObjectPickerStore(s => s.setAreaMode);
	const setAoiPolygon = useObjectPickerStore(s => s.setAoiPolygon);
	const aoiPolygon = useObjectPickerStore(s => s.aoiPolygon);

	const selectEntireProject = () => {
		setAoiPolygon(null);
		setAreaMode(AREA_MODE.ENTIRE);
	};

	const hasDrawnArea = areaMode === AREA_MODE.DRAW && Boolean(aoiPolygon);
	const aoiStats = useMemo(
		() => (hasDrawnArea ? getAoiStats(aoiPolygon) : null),
		[hasDrawnArea, aoiPolygon]
	);
	const areaLabel = aoiStats ? formatAoiArea(aoiStats.squareMeters) : null;

	const drawDescription = hasDrawnArea
		? 'Your area is set. Click to redraw it.'
		: 'Draw an area on the map to search for objects only inside it.';

	return (
		<Flex vertical gap={16}>
			{sourceCollectionName?.trim() && (
				<Text strong style={{ fontSize: 14 }}>
					{sourceCollectionName.trim()}
				</Text>
			)}
			<Flex gap={8} wrap>
				{objects.map(node => (
					<Tag
						key={node.key}
						variant="outlined"
						style={{
							margin: 0,
							display: 'inline-flex',
							alignItems: 'center',
							gap: 6,
						}}>
						<ColorDot color={node.color} />
						{node.label}
					</Tag>
				))}
			</Flex>

			<Row gutter={16} data-tour="picker-area">
				<Col xs={24} md={12}>
					<AreaOptionCard
						icon={<LuPentagon />}
						title="Draw area of interest"
						description={drawDescription}
						selected={areaMode === AREA_MODE.DRAW}
						onSelect={onDraw}
					/>
				</Col>
				<Col xs={24} md={12}>
					<AreaOptionCard
						icon={<LuLayers />}
						title="Entire project"
						description="Search the whole project. Covering more ground makes the run take longer."
						selected={areaMode === AREA_MODE.ENTIRE}
						onSelect={selectEntireProject}
					/>
				</Col>
			</Row>

			{aoiStats && (
				<Card size="small" styles={{ body: { padding: '8px 12px' } }}>
					<Row align="middle" justify="space-between" wrap={false} gutter={8}>
						<Col flex="auto">
							<Flex align="center" gap={8}>
								<LuCheck style={{ color: token.colorPrimary }} />
								<Text>Drawn area · {areaLabel}</Text>
							</Flex>
						</Col>
						<Col flex="none">
							<Button size="small" onClick={onDraw}>
								Redraw
							</Button>
						</Col>
					</Row>
				</Card>
			)}
		</Flex>
	);
};

export default AreaStep;
